import { defineComponent as _defineComponent } from 'vue'
import { Customer } from '@/models/tenant';
import { Routes } from '@/router/routes';
import { customersService } from '@/services/tenants.service';
import CustomerProperties from './CustomerProperties.vue';
import CustomerUsers from './CustomerUsers.vue';
import CustomerDevices from '@/views/Devices.vue';
import CustomerShiftModels from '@/views/customers/CustomerShiftModels.vue';
import { User } from '@/models/user';
import { UserType } from '@/models/userType';
import CustomerBendPartCategoryConfig from '@/views/customers/CustomerBendPartCategoryConfig.vue';
import CustomerCutGasTypeConfig from '@/views/customers/CustomerCutGasTypeConfig.vue';
import CustomerAlerts from './CustomerAlerts.vue';
import CustomerMaterialGroups from '@/views/customers/CustomerMaterialGroups.vue';
import CustomerTarget from './CustomerTarget.vue';
import CustomerLocations from './CustomerLocations.vue';
import { ConsoleEnum } from '@/models/enums/ConsoleEnum';
import CustomerEmailReport from './CustomerEmailReport.vue';
import { usersService } from '@/services/users.service';
import { Logger } from '@/utils/logger';
import { hasCurrentUserPermission } from '@/utils/permissionUtils';
import { toastService } from '@/library-services/toast.service';
import { computed, onBeforeMount, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from '@/composables/router';
import { useSpinner } from '@/composables/misc';
import { useI18n } from '@/composables/i18n';
import { BetaFeature } from '@/models/enums/BetaFeature';

enum Tab {
  Properties,
  Devices,
  Users,
  Locations,
  ShiftModels,
  CutGas,
  BendPartCategory,
  MaterialGroups,
  Alerts,
  Targets,
  EmailReports,
}


export default /*#__PURE__*/_defineComponent({
  __name: 'CustomerDetail',
  setup(__props) {

const customer = reactive(Customer.GetEmpty());
const selectedTab = ref(Tab.Properties);

onBeforeMount(async () => {
  await redirectToCustomersPageIfRouteParameterIsMissing('id');
  Object.assign(customer, await fetchCustomer(+route.value.params.id));
});

async function fetchCustomer(id: number): Promise<Customer> {
  spinner.showSpinner();
  try {
    return await customersService.getById(id);
  } finally {
    spinner.removeSpinner();
  }
}

function onCustomerUpdate(updatedCustomer: Customer) {
  Object.assign(customer, updatedCustomer);
}

async function redirectToCustomersPageIfRouteParameterIsMissing(
  parameter: string,
) {
  if (!route.value.params[parameter]) {
    Logger.error(
      `Missing '${parameter}' route parameter. Redirecting to Tenants...`,
    );
    toastService.error(`Missing '${parameter}' route parameter`);
    await router.push({ name: Routes.Tenants });
    throw Error(
      `Missing '${parameter}' route parameter. Redirecting to Tenants...`,
    );
  }
}

const canAccessShifts = computed<boolean>(
  () => hasCurrentUserPermission(UserType.CustomerAdmin) && wccEnabled.value,
);

const canAccessCategoryConfiguration = computed<boolean>(
  () => hasCurrentUserPermission(UserType.CustomerAdmin) && wccEnabled.value,
);

const canAccessCutConfiguration = computed<boolean>(
  () => hasCurrentUserPermission(UserType.CustomerAdmin) && wccEnabled.value,
);

const canAccessMaterialGroups = computed<boolean>(
  () => hasCurrentUserPermission(UserType.CustomerAdmin) && wccEnabled.value,
);

const canAccessAlerts = computed<boolean>(
  () => hasCurrentUserPermission(UserType.CustomerAdmin) && wccEnabled.value,
);

const canAccessTargets = computed<boolean>(
  () =>
    hasCurrentUserPermission(UserType.CustomerAdmin) &&
    (wccEnabled.value || bcEnabled.value),
);

const canAccessEmailReports = computed<boolean>(
  () => hasCurrentUserPermission(UserType.CustomerAdmin) && wccEnabled.value,
);

const wccEnabled = computed<boolean>(
  () => !!user.value?.consoles.includes(ConsoleEnum.WCC) && customer.hasDatahub,
);

const bcEnabled = computed<boolean>(
  () =>
    customer.hasSmbs &&
    (user.value?.consoles.includes(ConsoleEnum.BC) ||
      user.value?.isGlobalAdmin),
);

const hasNewBendingConsole = computed<boolean>(() =>
  user.value.hasBetaFeature(BetaFeature.WorkCenterConsoleBend),
);

const user = computed<User>(() => usersService.store.current());

watch(
  () => user.value.isBetaEnabled,
  (isBetaEnabled: boolean, wasBetaEnabled: boolean) => {
    if (!wasBetaEnabled && isBetaEnabled) {
      if (selectedTab.value === Tab.BendPartCategory) {
        selectedTab.value = Tab.Properties;
      }
    }
  },
);

const route = useRoute();
const router = useRouter();
const spinner = useSpinner();
const { t } = useI18n();

return { __sfc: true,Tab, customer, selectedTab, fetchCustomer, onCustomerUpdate, redirectToCustomersPageIfRouteParameterIsMissing, canAccessShifts, canAccessCategoryConfiguration, canAccessCutConfiguration, canAccessMaterialGroups, canAccessAlerts, canAccessTargets, canAccessEmailReports, wccEnabled, bcEnabled, hasNewBendingConsole, user, route, router, spinner, t, CustomerProperties, CustomerUsers, CustomerDevices, CustomerShiftModels, CustomerBendPartCategoryConfig, CustomerCutGasTypeConfig, CustomerAlerts, CustomerMaterialGroups, CustomerTarget, CustomerLocations, CustomerEmailReport }
}

})